<template lang="pug">
#safety-confirm-list
  // title
  .d-flex.flex-row.justify-content-center.mb-3
    .page-title 安否確認一覧

  // safety confirm list
  SafetyConfirmTable(
    v-model="items"
    :fields="fields"
    :totalRows.sync="rows"
    :currentPage="currentPage"
    :perPage="perPage"
    :isBusy="isBusy"
    @onRegister="onRegister"
    @onDetail="onDetail"
  )
  // pagenation
  .d-flex.flex-row.justify-content-center(v-if="!isBusy")
    b-pagination.m-0(
      v-model="currentPage"
      :total-rows="rows"
      :per-page="perPage"
      :disabled="isBusy"
      @change="selectPage"
      hide-goto-end-buttons
    )
</template>

<script lang="ts">
import { Component, Inject, Vue } from "vue-property-decorator";
import { userStore, mobileSendSafetyConfirmDetailToOtherUserStore } from "@/store/store";
import { MobileSendSafetyConfirmDetailToOtherUserState } from "@/store/MobileSendSafetyConfirmDetailToOtherUser";
import { errorHandle } from "@/errorHandler";
import Setting from "../../../../public/setting.json";
import { MobileUrl } from "@/commons/constants/MobileUrl";

import SafetyConfirmsService, { SafetyConfirmsGetModel } from "@/commons/services/SafetyConfirms";

import SafetyConfirmTable, { SafetyConfirmTableField, SafetyConfirmTableItem } from "@/mobile/components/tables/SafetyConfirmTable.vue";

/**
 * ### 安否確認一覧コンポーネント
 */
@Component({
  components: {
    SafetyConfirmTable
  }
})
export default class SafetyConfirmList extends Vue {
  /** 安否確認サービス */
  @Inject("SafetyConfirmsService") safetyConfirmsService!: SafetyConfirmsService;

  /** 読込み中か否か */
  isBusy: boolean = true;

  /** 列名 */
  fields: Array<SafetyConfirmTableField> = [];
  /** 各行 */
  items: Array<SafetyConfirmTableItem> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** 現在取得している件数 */
  currentRows: number = 0;
  /** メッセージ履歴1ページあたりの表示数 */
  perPage: number = Number(Setting.safetyConfirm.perPage);
  /** ページングの全体行数 */
  rows: number = 0;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.safetyConfirm.pageRange;

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<SafetyConfirmTableField> = this.createFields();
    this.fields = fields;

    // 動的に変更する画面情報を作成する
    this.items = [];
    this.currentRows = this.items.length;
    this.currentPage = 1;
    await this.createView();

    // ページ番号の復元は画面初回表示時でのみ行う
    // pageRowが設定されるまえにcurrentPageを復元してもライブラリがページ遷移をしてくれないので情報取得後にページ番号を復元する
    this.currentPage = this.$route.params.page ? Number(this.$route.params.page) : 1;

    return;
  }

  /**
   * ### 列名を作成する
   */
  public createFields(): Array<SafetyConfirmTableField> {
    let fields: Array<SafetyConfirmTableField> = [];
    fields.push({ key: "safetyConfirm", label: "", sortable: false, sortDirection: "desc", class: "" });

    return fields;
  }

  /**
   * ### 画面表示情報を作成する
   */
  public async createView(): Promise<void> {
    this.isBusy = true;

    // 全体の行数を非同期で取得する
    this.setRows();

    // テーブルに渡す各プロパティを作成する
    const safetyConfirms: Array<SafetyConfirmsGetModel> = await this.getSafetyConfirms();
    const items: Array<SafetyConfirmTableItem> = this.createItems(safetyConfirms);
    this.items = this.items.concat(items);
    this.currentRows = this.items.length;

    this.isBusy = false;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.safetyConfirmsService.getCount(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 安否確認を取得する
   */
  public async getSafetyConfirms(): Promise<Array<SafetyConfirmsGetModel>> {
    const startIndex: number = this.currentRows;
    // tableのライブラリでソートやフィルタリング、ページングを管理するので表示対象のよりも多めに取得しておく
    const endIndex: number = this.perPage * (this.currentPage * this.pageRange);
    const params: any = {
      startIndex: startIndex,
      endIndex: endIndex,
      order: "desc"
    };

    const response: Array<SafetyConfirmsGetModel> = await this.safetyConfirmsService.gets(userStore, params);

    return response;
  }

  /**
   * ### テーブルの行を作成する
   */
  public createItems(safetyConfirms: Array<SafetyConfirmsGetModel>): Array<SafetyConfirmTableItem> {
    let items: Array<SafetyConfirmTableItem> = [];

    for (const safetyConfirm of safetyConfirms) {
      items.push(safetyConfirm);
    }

    return items;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;

    // 初期表示時点では一定数しか情報を取得していないので、既に情報が取得されていなければ取得する
    const newPageRows = this.perPage * (this.currentPage - 1);
    if (this.currentRows <= newPageRows) {
      await this.createView();
    }

    return;
  }

  /**
   * ### 安否確認入力依頼のメッセージ送信(施設関係者)画面へ遷移する
   */
  public onRegister(item: SafetyConfirmTableItem): void {
    this.$router.push({ name: MobileUrl.SendSafetyConfirmToOtherUserInput, params: { id: String(item.id) } });
    return;
  }

  /**
   * ### 安否確認入力依頼のメッセージ送信(施設関係者)詳細画面へ遷移する
   */
  public onDetail(item: SafetyConfirmTableItem): void {
    const state: MobileSendSafetyConfirmDetailToOtherUserState = {
      ...item,
      exist: true
    };
    mobileSendSafetyConfirmDetailToOtherUserStore.updateMobileSendSafetyConfirmDetailToOtherUserState({ state: state });
    this.$router.push({ name: MobileUrl.SendSafetyConfirmToOtherUserDetail, params: { id: String(item.id) } });
    return;
  }
}
</script>
